import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "text-button inline-flex items-center justify-center cursor-pointer ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 btn-hover-scale transition-colors duration-200 ease-in-out text-center max-w-full [word-wrap:break-word] [overflow-wrap:break-word] [-webkit-hyphens:auto] [-ms-hyphens:auto] [hyphens:auto]",
  {
    variants: {
      variant: {
        black:
          "text-white [--btn-bg:hsl(var(--gray-100))] [--btn-bg-hover:hsl(var(--gray-90))]",
        sun: "text-black-100 [--btn-bg:hsl(var(--sun-100))] hover:text-white [--btn-bg-hover:hsl(var(--gray-100))]",
        white:
          "text-black-95 [--btn-bg:hsl(var(--white-100))] [--btn-bg-hover:hsl(var(--sand-75))]",
        transparent:
          "text-black-100 [--btn-bg:transparent] [--btn-bg-hover:hsl(var(--sand-75))]",
        outline:
          "border border-white-80 box-border [--btn-bg:transparent] [--btn-bg-hover:hsl(var(--sand-75))] hover:border-white-90",
        link: "text-black-100 underline-offset-4 hover:underline [--btn-bg:transparent]",
        linkUnderline:
          "text-black-100 underline-offset-4 underline [--btn-bg:transparent]",
        mylife:
          "text-white [--btn-bg:hsl(var(--mylife-green-100))] [--btn-bg-hover:hsl(var(--mylife-green-90))]",
        dexcom:
          "text-white [--btn-bg:hsl(var(--dexcom-green-100))] [--btn-bg-hover:hsl(var(--dexcom-green-90))]",
        minimed:
          "text-white [--btn-bg:hsl(var(--minimed-blue-100))] [--btn-bg-hover:hsl(var(--minimed-blue-90))]",

        destructive:
          "text-destructive-foreground [--btn-bg:hsl(var(--destructive))] [--btn-bg-hover:hsl(var(--destructive)/0.9)]",
        ghost:
          "hover:text-accent-foreground [--btn-bg:transparent] [--btn-bg-hover:hsl(var(--accent))]",
      },
      size: {
        sm: "h-[46px] py-3 px-5 gap-x-2 rounded-full",
        lg: "py-4 px-8 gap-x-3 rounded-full",
        icon: "h-[46px] w-[46px] rounded-full",
        admin: "h-9 py-1.5 px-3 gap-x-2 text-sm rounded-md",
      },
    },
    defaultVariants: {
      variant: "black",
      size: "sm",
    },
  }
);

export type ButtonVariant = NonNullable<
  VariantProps<typeof buttonVariants>["variant"]
>;

export type ButtonSize = NonNullable<
  VariantProps<typeof buttonVariants>["size"]
>;

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        lang="de"
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
